import { supabase } from '../lib/supabase';

export const payrollService = {
  async getStaffWageRates(tenantId, staffId = null) {
    try {
      let query = supabase?.from('staff_wage_rates')?.select('*')?.eq('tenant_id', tenantId)?.eq('is_active', true)?.order('effective_date', { ascending: false });

      if (staffId) {
        query = query?.eq('staff_id', staffId);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Get staff wage rates error:', error);
      throw error;
    }
  },

  async createWageRate(wageRateData) {
    try {
      const { data, error } = await supabase?.from('staff_wage_rates')?.insert([wageRateData])?.select()?.single();
      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Create wage rate error:', error);
      throw error;
    }
  },

  async updateWageRate(wageRateId, updates) {
    try {
      const { data, error } = await supabase?.from('staff_wage_rates')?.update(updates)?.eq('id', wageRateId)?.select()?.single();
      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Update wage rate error:', error);
      throw error;
    }
  },

  async calculatePayrollFromAttendance(tenantId, staffId, periodStart, periodEnd) {
    try {
      const { data, error } = await supabase?.rpc('calculate_payroll_from_attendance', {
        p_tenant_id: tenantId,
        p_staff_id: staffId,
        p_period_start: periodStart,
        p_period_end: periodEnd
      });
      if (error) throw error;
      return data?.[0] || null;
    } catch (error) {
      console.error('Calculate payroll from attendance error:', error);
      throw error;
    }
  },

  async calculateTaxDeductions(tenantId, grossPay) {
    try {
      const { data, error } = await supabase?.rpc('calculate_tax_deductions', {
        p_tenant_id: tenantId,
        p_gross_pay: grossPay
      });
      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Calculate tax deductions error:', error);
      throw error;
    }
  },

  async createPayrollRecord(payrollData) {
    try {
      const { data, error } = await supabase?.from('payroll_records')?.insert([payrollData])?.select()?.single();
      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Create payroll record error:', error);
      throw error;
    }
  },

  async addPayrollDeductions(payrollId, deductions) {
    try {
      const deductionRecords = deductions?.map(d => ({
        payroll_id: payrollId,
        ...d
      }));
      const { data, error } = await supabase?.from('payroll_deductions')?.insert(deductionRecords)?.select();
      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Add payroll deductions error:', error);
      throw error;
    }
  },

  async getPayrollRecords(tenantId, filters = {}) {
    try {
      let query = supabase?.from('payroll_records')?.select(`
          *,
          user_profiles!payroll_records_staff_id_fkey(id, full_name, email, avatar_url),
          payroll_deductions(*)
        `)?.eq('tenant_id', tenantId)?.order('pay_period_end', { ascending: false });

      if (filters?.staffId) {
        query = query?.eq('staff_id', filters?.staffId);
      }
      if (filters?.status) {
        query = query?.eq('payment_status', filters?.status);
      }
      if (filters?.periodStart) {
        query = query?.gte('pay_period_start', filters?.periodStart);
      }
      if (filters?.periodEnd) {
        query = query?.lte('pay_period_end', filters?.periodEnd);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Get payroll records error:', error);
      throw error;
    }
  },

  async updatePayrollRecord(payrollId, updates) {
    try {
      const { data, error } = await supabase?.from('payroll_records')?.update(updates)?.eq('id', payrollId)?.select()?.single();
      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Update payroll record error:', error);
      throw error;
    }
  },

  async approvePayroll(payrollId, approverId) {
    try {
      const { data, error } = await supabase?.from('payroll_records')?.update({
          payment_status: 'approved',
          approved_by: approverId,
          approved_at: new Date()?.toISOString()
        })?.eq('id', payrollId)?.select()?.single();
      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Approve payroll error:', error);
      throw error;
    }
  },

  async getPayrollAlerts(tenantId, filters = {}) {
    try {
      let query = supabase?.from('payroll_alerts')?.select(`
          *,
          user_profiles!payroll_alerts_staff_id_fkey(full_name, email)
        `)?.eq('tenant_id', tenantId)?.order('created_at', { ascending: false });

      if (filters?.isResolved !== undefined) {
        query = query?.eq('is_resolved', filters?.isResolved);
      }
      if (filters?.severity) {
        query = query?.eq('severity', filters?.severity);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Get payroll alerts error:', error);
      throw error;
    }
  },

  async createPayrollAlert(alertData) {
    try {
      const { data, error } = await supabase?.from('payroll_alerts')?.insert([alertData])?.select()?.single();
      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Create payroll alert error:', error);
      throw error;
    }
  },

  async resolvePayrollAlert(alertId, resolverId) {
    try {
      const { data, error } = await supabase?.from('payroll_alerts')?.update({
          is_resolved: true,
          resolved_by: resolverId,
          resolved_at: new Date()?.toISOString()
        })?.eq('id', alertId)?.select()?.single();
      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Resolve payroll alert error:', error);
      throw error;
    }
  },

  async getTaxConfiguration(tenantId) {
    try {
      const { data, error } = await supabase?.from('payroll_tax_config')?.select('*')?.eq('tenant_id', tenantId)?.eq('is_active', true)?.order('tax_category');
      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Get tax configuration error:', error);
      throw error;
    }
  },

  async updateTaxConfiguration(configId, updates) {
    try {
      const { data, error } = await supabase?.from('payroll_tax_config')?.update(updates)?.eq('id', configId)?.select()?.single();
      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Update tax configuration error:', error);
      throw error;
    }
  },

  async getStaffAttendanceSummary(tenantId, staffId, periodStart, periodEnd) {
    try {
      const { data, error } = await supabase?.from('staff_attendance')?.select('*')?.eq('tenant_id', tenantId)?.eq('user_id', staffId)?.gte('check_in_time', periodStart)?.lte('check_in_time', periodEnd)?.order('check_in_time', { ascending: false });
      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Get staff attendance summary error:', error);
      throw error;
    }
  },

  async getPayrollStats(tenantId, periodStart, periodEnd) {
    try {
      const { data, error } = await supabase?.from('payroll_records')?.select('*')?.eq('tenant_id', tenantId)?.gte('pay_period_start', periodStart)?.lte('pay_period_end', periodEnd);

      if (error) throw error;

      const stats = {
        totalPayroll: data?.reduce((sum, record) => sum + parseFloat(record?.gross_pay || 0), 0),
        totalDeductions: data?.reduce((sum, record) => sum + parseFloat(record?.total_deductions || 0), 0),
        totalNetPay: data?.reduce((sum, record) => sum + parseFloat(record?.net_pay || 0), 0),
        recordCount: data?.length,
        pendingApprovals: data?.filter(r => r?.payment_status === 'pending')?.length,
        approvedCount: data?.filter(r => r?.payment_status === 'approved')?.length,
        paidCount: data?.filter(r => r?.payment_status === 'paid')?.length
      };

      return stats;
    } catch (error) {
      console.error('Get payroll stats error:', error);
      throw error;
    }
  }
};